import React, { useRef, useState, useEffect, useCallback } from 'react';
import ChevronIcon from './components/icons/ChevronIcon';

/**
 * Horizontal scroll row with optional prev/next controls on larger screens.
 * Mobile/tablet: touch scroll + scrollbar only. Desktop (lg+): side arrows when content overflows.
 */
export default function HorizontalScrollStrip({
  children,
  className = '',
  trackClassName = '',
  ariaLabel = 'Scrollable list',
}) {
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 2;
    setHasOverflow(overflow);
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    window.addEventListener('resize', updateScrollState);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, children]);

  const scrollByPage = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth * 0.85, 240);
    el.scrollBy({ left: direction === 'next' ? step : -step, behavior: 'smooth' });
  };

  const showNav = hasOverflow;

  return (
    <div className={`horizontal-scroll-strip${className ? ` ${className}` : ''}`}>
      {showNav ? (
        <button
          type="button"
          className="horizontal-scroll-strip__nav horizontal-scroll-strip__nav--prev pdp-gallery__nav-btn"
          onClick={() => scrollByPage('prev')}
          disabled={!canScrollLeft}
          aria-label="Scroll left"
        >
          <ChevronIcon direction="left" />
        </button>
      ) : null}

      <div
        ref={trackRef}
        className={trackClassName}
        role="region"
        aria-label={ariaLabel}
        tabIndex={showNav ? 0 : undefined}
      >
        {children}
      </div>

      {showNav ? (
        <button
          type="button"
          className="horizontal-scroll-strip__nav horizontal-scroll-strip__nav--next pdp-gallery__nav-btn"
          onClick={() => scrollByPage('next')}
          disabled={!canScrollRight}
          aria-label="Scroll right"
        >
          <ChevronIcon direction="right" />
        </button>
      ) : null}
    </div>
  );
}
